import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getClientById, getObjectById, subscribeToObjectsByClient, subscribeToInspectionsByObject, getInspectionsByObject, getObjectMissing, getAssessmentMissing, subscribeToDDLFullItemsByDropdownId, subscribeToDDLCollections, subscribeToDropdownRegistrations, archiveObject, updateInspectionData } from '../utils/storage';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { ReadOnlyContext } from '../contexts/ReadOnlyContext';
import { useAuth } from '../contexts/AuthContext';
import CreateInspectionModal from './CreateInspectionModal';
import EditObjectModal from './EditObjectModal';
import GeneralInfoModal from './GeneralInfoModal';
import AssessmentSettingsModal from './AssessmentSettingsModal';
import PrintObjectReportModal from './PrintObjectReportModal';
import AssignReporterModal from './AssignReporterModal';
import { getPermissions } from '../utils/permissions';
import LoadingScreen from './LoadingScreen';
import { DetailPageHeader } from './templates';
import { computeDeliveryDate } from '../utils/inspectionStatus';
import { getL3Certification } from '../utils/l3Certification';
import { filenameFromResponse, saveBlob, previewAndSaveBlob } from '../utils/download';
import './ClientDetail.css';
import './ObjectDetail.css';
import './InspectionStatus.css';

// Returns true when all required general-info fields are present.
// L3 Inspector is required only when Involvement of L3 Inspector is checked.
// Object photo is not required (optional).
function isAssessmentInfoFilled(gi) {
  if (!gi) return false;
  const baseFilled = !!(
    gi.inspectionStandard &&
    gi.inspectionNumber   &&
    gi.startDate          &&
    gi.endDate            &&
    gi.accountManager     &&
    gi.leadNDTOperator
  );
  if (!baseFilled) return false;
  if (gi.involvementL3Inspector && !gi.l3Inspector) return false;
  return true;
}

// Format a stored date string ("YYYY-MM-DD") for display
function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// "EEMUA 159 (No L3 Inspector involved)" or
// "EEMUA 159 (Name [CERT-123 • 31 Dec 2027])".
// Cert details come from the standard → L3 inspector → certification DDL chain;
// parts that aren't registered are simply left out.
function fmtInspectionStandard(gi, ddlCollections) {
  const std = gi.inspectionStandard || '';
  if (!gi.involvementL3Inspector) return `${std} (No L3 Inspector involved)`;
  const cert = getL3Certification(ddlCollections, gi.inspectionStandard, gi.l3Inspector);
  const certParts = cert
    ? [cert.certName, cert.expiryStr ? fmtDate(cert.expiryStr) : ''].filter(Boolean).join(' • ')
    : '';
  return `${std} (${gi.l3Inspector}${certParts ? ` [${certParts}]` : ''})`;
}

// Look up the sub-item label(s) for a given primary DDL label (used for Certification)
function getCertification(items, operatorLabel) {
  if (!operatorLabel || !items.length) return null;
  const found = items.find((it) => it.label === operatorLabel);
  if (!found || !found.children || !found.children.length) return null;
  return found.children.map((c) => c.label).join(', ');
}

export default function ObjectDetail() {
  const { id: clientId, objectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setBreadcrumb, clearBreadcrumb } = useBreadcrumb();
  const { userProfile } = useAuth();
  const isAdmin = !!userProfile?.roles?.includes('admin');
  const perms = getPermissions(userProfile?.roles || []);
  // Inspection to (re)assign a reporter for; null when the modal is closed.
  const [assignInsp, setAssignInsp] = useState(null);
  // Archived objects render read-only; ?view=summary shows only the object card.
  const [archiveStage, setArchiveStage] = useState(null); // null | 'blocked' | 'confirm'
  const [archiving, setArchiving] = useState(false);
  const [client, setClient] = useState(null);
  const [object, setObject] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [allObjects, setAllObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInspectionModal,  setShowInspectionModal]  = useState(false);
  const [showEditModal,        setShowEditModal]        = useState(false);
  const [showAssessmentInfoModal, setShowAssessmentInfoModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  // When true, opening GeneralInfoModal was triggered by + Add Inspection;
  // after saving the general info we immediately open the inspection modal.
  const [pendingAddInspection, setPendingAddInspection] = useState(false);
  // Full items for NDT operator DDL (needed to look up certifications)
  const [operatorItems, setOperatorItems] = useState([]);
  const [pageReady, setPageReady] = useState(false);

  // Assessment section kebab menu
  const [showAssessmentMenu,  setShowAssessmentMenu]  = useState(false);
  const [showSettingsModal,   setShowSettingsModal]   = useState(false);
  const assessmentMenuRef = useRef(null);

  // Close Assessment kebab menu on outside click or Escape
  useEffect(() => {
    if (!showAssessmentMenu) return;
    function handleOutside(e) {
      if (assessmentMenuRef.current && !assessmentMenuRef.current.contains(e.target)) {
        setShowAssessmentMenu(false);
      }
    }
    function handleEsc(e) {
      if (e.key === 'Escape') setShowAssessmentMenu(false);
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [showAssessmentMenu]);

  // Immediately re-fetch object from API (don't wait for 15s poll)
  async function refreshObject() {
    try {
      const o = await getObjectById(objectId);
      if (o) setObject(o);
    } catch (err) {
      console.error('Failed to refresh object:', err);
    }
  }

  function handleAddInspectionClick() {
    if (isAssessmentInfoFilled(object?.generalInfo)) {
      setShowInspectionModal(true);
    } else {
      setPendingAddInspection(true);
      setShowAssessmentInfoModal(true);
    }
  }

  function handleAssessmentInfoClose() {
    setShowAssessmentInfoModal(false);
    setPendingAddInspection(false);
    refreshObject();
  }

  function handleAssessmentInfoSaved() {
    refreshObject();
    if (pendingAddInspection) {
      setShowInspectionModal(true);
    }
  }

  // Load client and object
  useEffect(() => {
    async function load() {
      const [c, o] = await Promise.all([
        getClientById(clientId),
        getObjectById(objectId),
      ]);
      if (!c || !o) {
        navigate(`/client/${clientId}`);
        return;
      }
      setClient(c);
      setObject(o);
      setLoading(false);
    }
    load();
  }, [clientId, objectId, navigate]);

  // Breadcrumb: TestSite > ObjectTag
  useEffect(() => {
    if (client && object) {
      setBreadcrumb([
        { label: client.name, path: `/client/${clientId}` },
        { label: object.objectTag, path: null },
      ]);
    }
    return () => clearBreadcrumb();
  }, [client, object, clientId, setBreadcrumb, clearBreadcrumb]);

  // Real-time listener for objects in this client (for chevron navigation)
  useEffect(() => {
    const unsubscribe = subscribeToObjectsByClient(clientId, (objectList) => {
      setAllObjects(objectList);
      // Also update current object from the list
      const current = objectList.find((o) => o.id === objectId);
      if (current) {
        setObject(current);
      }
    });
    return () => unsubscribe();
  }, [clientId, objectId]);

  // Real-time listener for inspections of this object
  useEffect(() => {
    const unsubscribe = subscribeToInspectionsByObject(objectId, (inspectionList) => {
      setInspections(inspectionList);
    });
    return () => unsubscribe();
  }, [objectId]);

  // Full DDL items for Lead NDT Operator (to look up certification sub-items)
  useEffect(() => subscribeToDDLFullItemsByDropdownId('general_leadNDTOperator', setOperatorItems), []);

  // Pre-load all DDL collections so assessment modals open with data already available
  const [ddlCollections, setDdlCollections] = useState([]);
  useEffect(() => subscribeToDDLCollections(setDdlCollections), []);

  // Pre-load dropdown registrations (chain/link/none configs) for GeneralInfoModal
  const [ddlConfigs, setDdlConfigs] = useState({});
  useEffect(() => subscribeToDropdownRegistrations(setDdlConfigs), []);


  // One-shot refresh used after creating an inspection — bypasses
  // the 15s poll tick from subscribeToInspectionsByObject so the new
  // row appears immediately.
  async function refreshInspections() {
    try {
      const fresh = await getInspectionsByObject(objectId);
      setInspections(fresh);
    } catch (err) {
      console.error('refreshInspections failed:', err);
    }
  }

  function handleInspectionCreated() {
    setShowInspectionModal(false);
    refreshInspections();
  }

  if (!pageReady) {
    return (
      <LoadingScreen
        isLoading={loading || !client || !object}
        onDone={() => setPageReady(true)}
      />
    );
  }

  // Chevron navigation: objects within same client
  // Sort objects the same way as ClientDetail table (by startDate ascending, no-date last)
  const sortedAllObjects = [...allObjects].sort((a, b) => {
    const da = a.generalInfo?.startDate || '';
    const db = b.generalInfo?.startDate || '';
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da.localeCompare(db);
  });
  const currentIndex = sortedAllObjects.findIndex((o) => o.id === objectId);
  const prevObject = currentIndex > 0 ? sortedAllObjects[currentIndex - 1] : null;
  const nextObject = currentIndex < sortedAllObjects.length - 1 ? sortedAllObjects[currentIndex + 1] : null;

  // Delivery date — computed from assessment start + reporting period
  // ('2 Weeks' when unset, matching the backend and the dashboard)
  const reportingPeriod = object.settings?.reportingPeriod || '2 Weeks';
  const deliveryDate = computeDeliveryDate(
    object.generalInfo?.startDate,
    reportingPeriod,
  );
  // Label for the "Due date:" suffix in the section header
  const dueDateLabel = (() => {
    const rp = reportingPeriod;
    if (/no\s*deadline/i.test(rp)) return 'Not applicable';
    if (deliveryDate) return fmtDate(deliveryDate);
    return null;
  })();

  // Design spec shorthand (safe access)
  const ds = object.designSpec || {};

  // Build material display string
  const mat2Valid = ds.material2 && ds.material2 !== 'Not applicable';
  const mat2Str   = mat2Valid
    ? `, ${ds.material2}${ds.material2Type && ds.material2Type !== 'Not applicable' ? ` (${ds.material2Type})` : ''}`
    : '';
  const matStr = ds.material1
    ? `${ds.material1}${ds.material1Type ? ` (${ds.material1Type})` : ''}${mat2Str}`
    : '';

  const readOnly = !!object.archivedAt;
  const objectInfoOnly = searchParams.get('view') === 'summary';  // archived object node → object card only

  function handleArchiveClick() {
    const eligible = inspections.length > 0 && inspections.every((i) => i.effectiveStatus === 'Delivery');
    setArchiveStage(eligible ? 'confirm' : 'blocked');
  }
  async function handleArchiveConfirmed() {
    if (archiving) return;
    setArchiving(true);
    try { await archiveObject(objectId); navigate('/archived-projects'); }
    catch { setArchiving(false); }
  }
  // Admin-only: archive despite non-Delivery inspections. The backend forces
  // those inspections to Delivery, then archives.
  async function handleArchiveAnyway() {
    if (archiving) return;
    setArchiving(true);
    try { await archiveObject(objectId, true); navigate('/archived-projects'); }
    catch { setArchiving(false); }
  }

  // Assign a reporter to an inspection (from the inspection-table cell). Merges
  // the choice into the inspection's form_data; assigned_to() then reports the
  // chosen name (which also trickles to TerraFlow via the update webhook).
  async function handleAssignReporter(user) {
    const insp = assignInsp;
    if (!insp) return;
    const newFormData = {
      ...(insp.formData || {}),
      assignedReporter: { name: user.name, email: user.email },
    };
    await updateInspectionData(insp.id, { formData: newFormData });
    setInspections((prev) => prev.map((i) => (
      i.id === insp.id ? { ...i, assignedTo: user.name, formData: newFormData } : i
    )));
    setAssignInsp(null);
  }

  return (
    <ReadOnlyContext.Provider value={readOnly}>
    <div className="client-detail">
      <DetailPageHeader
        title={object.objectTag || '—'}
        onBack={readOnly ? () => navigate('/archived-projects') : () => navigate(`/client/${clientId}`)}
        backLabel={readOnly ? '← Back to Archived Projects' : `← Back to ${client.name}`}
        onPrev={!readOnly && prevObject ? () => navigate(`/client/${clientId}/object/${prevObject.id}`) : undefined}
        onNext={!readOnly && nextObject ? () => navigate(`/client/${clientId}/object/${nextObject.id}`) : undefined}
        canPrev={!readOnly && !!prevObject}
        canNext={!readOnly && !!nextObject}
        prevTitle={prevObject?.objectTag}
        nextTitle={nextObject?.objectTag}
        actions={objectInfoOnly ? null : (
          <button
            className="btn btn-primary btn-sm insp-print-report-btn"
            onClick={() => setShowPrintModal(true)}
            disabled={isPrinting || inspections.length === 0}
            title={inspections.length === 0 ? 'No inspections to print' : 'Print combined report'}
          >
            {isPrinting ? (
              <span className="insp-print-dots"><span /><span /><span /></span>
            ) : 'Print Report'}
          </button>
        )}
      >
        <div className="detail-info">
          <div className="od-info-header-row">
            <div className="od-info-title" style={{ marginBottom: 0 }}>Object Information</div>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowEditModal(true)}>
              {readOnly ? 'Show more' : 'Edit'}
            </button>
          </div>
          <div className="od-info-grid">

            {/* ── Column 1: Type, Spec, Attrs ── */}
            <div className="od-info-col">
              <div className="od-info-cell">
                <span className="od-info-label">Object type:</span>
                <span className="od-info-value">{object.type || '—'}</span>
              </div>
              <div className="od-info-cell">
                <span className="od-info-label">Object specification:</span>
                <span className="od-info-value">{ds.objectSpec || '—'}</span>
              </div>
              {(ds.attributeA || ds.attributeAType) && (
                <div className="od-info-cell">
                  <span className="od-info-label">{ds.attributeA || '—'}:</span>
                  <span className="od-info-value">{ds.attributeAType || '—'}</span>
                </div>
              )}
              {(ds.attributeB || ds.attributeBType) && (
                <div className="od-info-cell">
                  <span className="od-info-label">{ds.attributeB || '—'}:</span>
                  <span className="od-info-value">{ds.attributeBType || '—'}</span>
                </div>
              )}
            </div>

            {/* ── Column 2: Dims, Materials, Year ── */}
            <div className="od-info-col">
              {ds.dimensionA && (
                <div className="od-info-cell">
                  <span className="od-info-label">{ds.dimensionA}&nbsp;(mm):</span>
                  <span className="od-info-value">{ds.dimASize || '—'}</span>
                </div>
              )}
              {ds.dimensionB && (
                <div className="od-info-cell">
                  <span className="od-info-label">{ds.dimensionB}&nbsp;(mm):</span>
                  <span className="od-info-value">{ds.dimBSize || '—'}</span>
                </div>
              )}
              {ds.material1 && (
                <div className="od-info-cell">
                  <span className="od-info-label">Construction material(s):</span>
                  <span className="od-info-value">{matStr}</span>
                </div>
              )}
              {ds.yearOfConstruction && (
                <div className="od-info-cell">
                  <span className="od-info-label">Year of construction:</span>
                  <span className="od-info-value">{ds.yearOfConstruction}</span>
                </div>
              )}
            </div>

          </div>
        </div>

        {(() => {
          const missing = getObjectMissing(object);
          if (missing.length === 0) return null;
          return (
            <div className="insp-incomplete-alert">
              <span className="insp-incomplete-icon">&#9888;</span>
              Incomplete &mdash; {missing.join(', ')}
            </div>
          );
        })()}
      </DetailPageHeader>

      {/* ── Assessment Information section ───────────────────── */}
      {(() => {
        if (objectInfoOnly) return null;   // archived object node shows only the object card
        const gi = object.generalInfo || {};
        const filled = isAssessmentInfoFilled(gi);
        return (
          <section className="assessment-section">
            <div className="assessment-section-header">
              <span className="assessment-section-title">
                Assessment Information
                {dueDateLabel && (
                  <span className="assessment-due-date">&nbsp;(Due date: {dueDateLabel})</span>
                )}
              </span>

              {readOnly ? (
                /* Archived → read-only "Show more" (no settings, no kebab) */
                <button className="btn btn-secondary btn-sm assessment-showmore-btn" onClick={() => setShowAssessmentInfoModal(true)}>
                  Show more
                </button>
              ) : (
                <div className="assessment-header-actions">
                  {/* Archive project — square icon, revealed on card hover, left of the kebab */}
                  <button
                    className="assessment-archive-btn"
                    title="Archive project"
                    aria-label="Archive project"
                    onClick={handleArchiveClick}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="4" rx="1" />
                      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
                      <path d="M10 12h4" />
                    </svg>
                  </button>

                  {/* Kebab menu ⋮ */}
                  <div className="assessment-kebab-wrapper" ref={assessmentMenuRef}>
                    <button
                      className="btn-kebab"
                      title="Options"
                      onClick={() => setShowAssessmentMenu((v) => !v)}
                    >
                      ⋮
                    </button>
                    {showAssessmentMenu && (
                      <div className="assessment-kebab-menu">
                        <button
                          className="assessment-kebab-item"
                          onClick={() => {
                            setShowAssessmentMenu(false);
                            setPendingAddInspection(false);
                            setShowAssessmentInfoModal(true);
                          }}
                        >
                          {filled ? 'Edit' : 'Fill In'}
                        </button>
                        <button
                          className="assessment-kebab-item"
                          onClick={() => {
                            setShowAssessmentMenu(false);
                            setShowSettingsModal(true);
                          }}
                        >
                          Settings
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {filled ? (
              <div className="assessment-grid">
                <div className="assessment-cell">
                  <span className="od-info-label">Inspection Standard:</span>
                  <span className="od-info-value">{fmtInspectionStandard(gi, ddlCollections)}</span>
                </div>
                <div className="assessment-cell">
                  <span className="od-info-label">Project Number:</span>
                  <span className="od-info-value">{gi.inspectionNumber}</span>
                </div>
                <div className="assessment-cell">
                  <span className="od-info-label">Account Manager:</span>
                  <span className="od-info-value">{gi.accountManager}</span>
                </div>
                <div className="assessment-cell">
                  <span className="od-info-label">Lead NDT Operator:</span>
                  <span className="od-info-value">{gi.leadNDTOperator}</span>
                </div>
                <div className="assessment-cell">
                  <span className="od-info-label">Inspection Period:</span>
                  <span className="od-info-value">
                    {fmtDate(gi.startDate)}
                    {gi.startDate !== gi.endDate && <> &rarr; {fmtDate(gi.endDate)}</>}
                  </span>
                </div>
                <div className="assessment-cell">
                  <span className="od-info-label">Certification:</span>
                  <span className="od-info-value">
                    {getCertification(operatorItems, gi.leadNDTOperator) || '—'}
                  </span>
                </div>
              </div>
            ) : (
              <p className="assessment-empty">
                Assessment information has not been filled in yet. This is required before adding inspections.
              </p>
            )}
            {(() => {
              const missing = getAssessmentMissing(gi);
              if (missing.length === 0) return null;
              return (
                <div className="insp-incomplete-alert">
                  <span className="insp-incomplete-icon">&#9888;</span>
                  Incomplete &mdash; {missing.join(', ')}
                </div>
              );
            })()}
          </section>
        );
      })()}

      {(() => {
        if (objectInfoOnly || readOnly) return null;   // no add-inspection when archived / object-only
        const existingTypeCodes = inspections.map((i) => i.type);
        const allAdded = new Set(existingTypeCodes).size >= 7;
        return (
          <div className="detail-toolbar detail-toolbar-right">
            <button
              className="btn btn-primary"
              onClick={handleAddInspectionClick}
              disabled={allAdded}
              title={allAdded ? 'All inspection types have been added' : undefined}
            >
              + Add Inspection
            </button>
          </div>
        );
      })()}

      {!objectInfoOnly && (inspections.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h2>No inspections yet</h2>
          <p>Create the first inspection for this object.</p>
          <button className="btn btn-primary" onClick={handleAddInspectionClick}>
            + Add Inspection
          </button>
        </div>
      ) : (
        <div className="objects-table-wrapper">
          <table className="objects-table insp-table">
            <colgroup>
              <col className="col-insp-name" />
              <col className="col-insp-status" />
              <col className="col-insp-assigned" />
              <col className="col-insp-arrow" />
            </colgroup>
            <thead>
              <tr>
                <th>Inspection ({inspections.length})</th>
                <th>Status</th>
                <th>Assigned Under</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...inspections]
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map((insp) => (
                  <tr
                    key={insp.id}
                    className="objects-row"
                    onClick={() => navigate(`/client/${clientId}/object/${objectId}/inspection/${insp.id}`)}
                  >
                    <td className="insp-full-name">{insp.name || insp.type}</td>
                    <td>
                      <span className="insp-status-badge" data-status={insp.effectiveStatus}>
                        {insp.effectiveStatus}
                      </span>
                    </td>
                    <td className="insp-assigned-cell">
                      {(perms.canManageInspections && !readOnly) ? (
                        <span
                          className="insp-assign-text"
                          role="button"
                          tabIndex={0}
                          title="Assign reporter / operator"
                          onClick={(e) => { e.stopPropagation(); setAssignInsp(insp); }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); setAssignInsp(insp); }
                          }}
                        >
                          {insp.assignedTo || 'No Assignee'}
                        </span>
                      ) : (
                        <span>{insp.assignedTo || 'No Assignee'}</span>
                      )}
                    </td>
                    <td className="obj-chevron-cell">›</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ))}

      {showInspectionModal && (
        <CreateInspectionModal
          objectId={objectId}
          existingTypes={inspections.map((i) => i.type)}
          objectTag={object?.objectTag}
          siteName={client?.name}
          onClose={() => setShowInspectionModal(false)}
          onCreated={handleInspectionCreated}
        />
      )}

      {showEditModal && object && (
        <EditObjectModal
          object={object}
          onClose={() => { setShowEditModal(false); refreshObject(); }}
          onDeleted={() => navigate(`/client/${clientId}`)}
        />
      )}

      {showAssessmentInfoModal && object && (
        <GeneralInfoModal
          object={object}
          ddlCollections={ddlCollections}
          ddlConfigs={ddlConfigs}
          onClose={handleAssessmentInfoClose}
          onSaved={handleAssessmentInfoSaved}
        />
      )}

      {showPrintModal && (
        <PrintObjectReportModal
          inspections={inspections}
          onCancel={() => { if (!isPrinting) setShowPrintModal(false); }}
          onConfirm={async (orderedIds, format = 'pdf') => {
            setIsPrinting(true);
            try {
              const API_BASE = process.env.REACT_APP_API_URL || '/api';
              const res = await fetch(`${API_BASE}/reports/object/${objectId}/pdf`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ inspection_ids: orderedIds, format }),
              });
              if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.detail || `HTTP ${res.status}`);
              }
              const blob = await res.blob();
              // Backend name: "NDT Report - <site> - <object id> - <code>".
              if (format === 'docx') {
                // ZIP archive — force a download (browsers can't render zip
                // inline).
                saveBlob(blob, filenameFromResponse(res, 'object-report-docx.zip'));
              } else {
                // PDF — open a preview tab AND force a correctly-named
                // download (a blob tab otherwise saves under a random UUID).
                previewAndSaveBlob(blob, filenameFromResponse(res, 'object-report.pdf'));
              }
              setShowPrintModal(false);
            } finally {
              setIsPrinting(false);
            }
          }}
        />
      )}

      {showSettingsModal && (
        <AssessmentSettingsModal object={object} ddlCollections={ddlCollections} onClose={() => { setShowSettingsModal(false); refreshObject(); }} />
      )}

      {assignInsp && (
        <AssignReporterModal
          inspectionLabel={`${object.objectTag || ''}: ${assignInsp.type || assignInsp.name || ''}`}
          currentEmail={assignInsp.formData?.assignedReporter?.email || ''}
          onCancel={() => setAssignInsp(null)}
          onAssign={handleAssignReporter}
        />
      )}

      {archiveStage === 'blocked' && (
        <div className="modal-overlay" onClick={() => !archiving && setArchiveStage(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Cannot archive yet</h2>
            <p className="modal-body-text">
              This object cannot be archived until <strong>all inspections are assigned to Delivery</strong>.
            </p>
            <div className="modal-actions">
              {/* Admin-only escape hatch: force every inspection to Delivery
                  and archive anyway. */}
              {isAdmin && (
                <button
                  className="btn btn-secondary"
                  onClick={handleArchiveAnyway}
                  disabled={archiving}
                  title="Set all inspections to Delivery and archive"
                >
                  {archiving ? 'Archiving…' : 'Archive anyway'}
                </button>
              )}
              <button className="btn btn-primary" onClick={() => setArchiveStage(null)} disabled={archiving}>OK</button>
            </div>
          </div>
        </div>
      )}

      {archiveStage === 'confirm' && (
        <div className="modal-overlay" onClick={() => !archiving && setArchiveStage(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Archive this project?</h2>
            <p className="modal-body-text">
              The object and its inspections move to <strong>Archived Projects</strong> (read-only) and disappear
              from the active app.
            </p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setArchiveStage(null)} disabled={archiving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleArchiveConfirmed} disabled={archiving}>
                {archiving ? 'Archiving…' : 'Okay'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ReadOnlyContext.Provider>
  );
}
